import { useEffect, useState, useContext, useRef, useCallback } from "react";
import { Grid, CircularProgress } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import useWebInvoicePrint from "helpers/InvoiceHelper";
import toast from "react-hot-toast";

import styles from "./styles.module.css";
import ButtonGray from "components/utils/CustomFields/ButtonGray";
import ButtonRed from "components/utils/CustomFields/ButtonRed";
import KOTInvoice from "components/utils/PrintInvoice/KOTInvoice";
import ThreeInchInvoice from "components/utils/PrintInvoice/ThreeInchInvoice";

import OnlineOrderContext from "context/OnlineOrderContext";
import OnlineOrderHelper from "helpers/OnlineOrderHelper";
import ShopContext from "context/ShopContext";
import ProductContext from "context/ProductContext";
import { TerminalContext } from "context/TerminalContext";
import NotificationContext from "context/NotificationContext";
import SalesContext from "context/SalesContext";

import * as SalesHelper from "helpers/SalesHelper";
import * as ProductHelper from "helpers/ProductHelper";
import * as NumberFormatHelper from "helpers/NumberFormatHelper";
import DateTimeHelpers from "helpers/DateTimeHelpers";
import ShopsHelper from "helpers/ShopsHelper";
import KotManager from "helpers/KotManager";
import { ONLINE_ORDER_STATUS, SALE_STATUS, ORDER_QR_PROCESSING_TYPES, KOT_STATUS } from "const";

function getOnlineOrderWarning(order, secret) {
  if (!order) return "Order not found";
  if (secret && order.secret !== secret) {
    return "Verification warning: Security secret mismatch!";
  }
  if (order.status === ONLINE_ORDER_STATUS.CANCELLED) {
    return "This order has been cancelled!";
  }
  if (order.status === ONLINE_ORDER_STATUS.REFUNDED) {
    return "This order has been refunded!";
  }
  return "";
}

const OnlineOrderDetailsPopup = ({ open, orderId, secret, onClose, onOrderProcessed }) => {
  const { selectedShop } = useContext(ShopContext);
  const { items, charges } = useContext(ProductContext);
  const { terminalSettings } = useContext(TerminalContext);
  const { refreshSales, updateSaleKotStatus } = useContext(SalesContext);
  const notificationContext = useContext(NotificationContext);
  const { getOnlineOrderById, updateOnlineOrderStatus } = useContext(OnlineOrderContext) || {};

  const [loading, setLoading] = useState(true);
  const [onlineOrder, setOnlineOrder] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [kotPrintSale, setKotPrintSale] = useState(null);
  const [billPrintSale, setBillPrintSale] = useState(null);

  const kotPrintRef = useRef(null);
  const invoicePrintRef = useRef(null);

  const handleHtmlKotPrint = useWebInvoicePrint({
    contentRef: kotPrintRef,
    documentTitle: `KOT-${kotPrintSale?.bill_no || "NA"}`,
  });

  const handleHtmlInvoicePrint = useWebInvoicePrint({
    contentRef: invoicePrintRef,
    documentTitle: `Invoice-${billPrintSale?.bill_no || "NA"}`,
  });

  const handleAcceptAndPrintKot = useCallback(
    async (order) => {
      const finalSale = await SalesHelper.buildOnlineOrderSaleObject(order, items, charges, selectedShop);

      // 1. Save sale to IndexedDB as draft so it gets local_id and appears on Active Orders page
      const local_id = await SalesHelper.createNewSaleInDB(finalSale);
      finalSale.local_id = local_id;
      await refreshSales?.(local_id);

      // 2. Save for HTML print template binding
      setKotPrintSale(finalSale);

      // Give state updates time to settle
      await new Promise((resolve) => setTimeout(resolve, 100));

      // 3. Print KOT if shop has valid subscription
      if (ShopsHelper.hasKotValidity(selectedShop)) {
        try {
          const categoriesList = await ProductHelper.getCategoriesFromDB();
          await KotManager.printKot({
            sale: finalSale,
            fallbackPrintFunction: handleHtmlKotPrint,
            categories: categoriesList,
          });
        } catch (e) {
          console.error("Failed to print KOT:", e);
        }
      }

      // 4. Update server online order status to PREPARING
      await updateOnlineOrderStatus(order.id, ONLINE_ORDER_STATUS.PREPARING);

      const updatedOrder = { ...order, status: ONLINE_ORDER_STATUS.PREPARING };
      setOnlineOrder(updatedOrder);
      onOrderProcessed?.(updatedOrder);
      onClose();
    },
    [
      items,
      charges,
      selectedShop,
      handleHtmlKotPrint,
      onOrderProcessed,
      onClose,
      refreshSales,
      updateOnlineOrderStatus,
    ],
  );

  const handleMarkAsReady = useCallback(
    async (order) => {
      // Update server online order status to READY
      await updateOnlineOrderStatus(order.id, ONLINE_ORDER_STATUS.READY);

      // Update local sale status if exists
      try {
        const activeSales = (await SalesHelper.getActiveSales()) || [];
        const sale = activeSales.find((s) => s.online_order_id === order.id);
        if (sale && sale.local_id) {
          await updateSaleKotStatus(sale, ONLINE_ORDER_STATUS.READY);
        }
      } catch (e) {
        console.error("Failed to update local sale status to READY:", e);
      }

      const updatedOrder = { ...order, status: ONLINE_ORDER_STATUS.READY };
      setOnlineOrder(updatedOrder);
      onOrderProcessed?.(updatedOrder);
      onClose();
    },
    [onOrderProcessed, onClose, updateOnlineOrderStatus, updateSaleKotStatus],
  );

  const handleDeliverAndPrintBill = useCallback(
    async (order) => {
      // Update server online order status to DELIVERED
      await updateOnlineOrderStatus(order.id, ONLINE_ORDER_STATUS.DELIVERED);

      let existingSale = await SalesHelper.getSaleByOnlineOrderId(order.id);
      if (!existingSale) {
        const activeSales = (await SalesHelper.getActiveSales()) || [];
        existingSale = activeSales.find((s) => s.online_order_id === order.id);
      }

      if (existingSale && existingSale.status === SALE_STATUS.PAID) {
        if (existingSale.local_id) {
          await SalesHelper.updateSaleKotStatus(existingSale.local_id, KOT_STATUS.DISPATCH);
          await refreshSales?.(existingSale.local_id);
        }

        const updatedOrder = { ...order, status: ONLINE_ORDER_STATUS.DELIVERED };
        setOnlineOrder(updatedOrder);
        onOrderProcessed?.(updatedOrder);
        onClose();
        return;
      }

      let finalSale;
      if (existingSale) {
        finalSale = {
          ...existingSale,
          status: SALE_STATUS.PAID,
          kot_status: KOT_STATUS.DISPATCH,
        };
        await SalesHelper.saveSaleInDB(finalSale);
      } else {
        finalSale = await SalesHelper.buildOnlineOrderSaleObject(order, items, charges, selectedShop);
        finalSale.status = SALE_STATUS.PAID;
        finalSale.kot_status = KOT_STATUS.DISPATCH;
        const local_id = await SalesHelper.createNewSaleInDB(finalSale);
        finalSale.local_id = local_id;
      }
      await refreshSales?.(finalSale.local_id);

      // Set print sale and trigger print
      setBillPrintSale(finalSale);
      await new Promise((resolve) => setTimeout(resolve, 100));

      try {
        await SalesHelper.saveAndProcess(finalSale, {
          shop: selectedShop,
          fallbackPrintFunction: handleHtmlInvoicePrint,
        });
      } catch (e) {
        console.error("Failed to print invoice:", e);
        try {
          await handleHtmlInvoicePrint?.();
        } catch (err) {
          console.error("Fallback print error:", err);
        }
      }

      const updatedOrder = { ...order, status: ONLINE_ORDER_STATUS.DELIVERED };
      setOnlineOrder(updatedOrder);
      onOrderProcessed?.(updatedOrder);
      onClose();
    },
    [
      items,
      charges,
      selectedShop,
      handleHtmlInvoicePrint,
      onOrderProcessed,
      onClose,
      refreshSales,
      updateOnlineOrderStatus,
    ],
  );

  const proceedOrder = useCallback(
    async (order) => {
      if (isProcessing) return;
      setIsProcessing(true);

      try {
        const orderStatus = (order.status || ONLINE_ORDER_STATUS.PENDING).toUpperCase();

        if (orderStatus === ONLINE_ORDER_STATUS.PENDING) {
          await handleAcceptAndPrintKot(order);
        } else if (orderStatus === ONLINE_ORDER_STATUS.PREPARING) {
          await handleMarkAsReady(order);
        } else if (orderStatus === ONLINE_ORDER_STATUS.READY) {
          await handleDeliverAndPrintBill(order);
        }
      } catch (err) {
        console.error("Error proceeding with online order:", err);
        toast.error(err.message || "Failed to proceed with online order.");
      } finally {
        setIsProcessing(false);
      }
    },
    [isProcessing, handleAcceptAndPrintKot, handleMarkAsReady, handleDeliverAndPrintBill],
  );

  // Initialize or fetch online order when opened
  useEffect(() => {
    if (!open || !orderId) {
      setOnlineOrder(null);
      setErrorMsg("");
      setLoading(true);
      return;
    }

    notificationContext?.stopRinging?.();

    const fetchOrder = async () => {
      // Check if the order already exists in context cache
      const cachedOrder = await getOnlineOrderById(orderId);
      if (cachedOrder) {
        setOnlineOrder(cachedOrder);
        setLoading(false);
        setErrorMsg(getOnlineOrderWarning(cachedOrder, secret));
        return;
      }

      setLoading(true);
      setErrorMsg("");
      try {
        const order = await OnlineOrderHelper.getOnlineOrder(orderId);
        setOnlineOrder(order);
        setErrorMsg(getOnlineOrderWarning(order, secret));
      } catch (err) {
        console.error("Failed to load online order:", err);
        setErrorMsg(err.message || "Failed to fetch online order from server.");
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [open, orderId, secret, notificationContext, getOnlineOrderById]);

  // Handle automatic processing if enabled in terminal settings
  useEffect(() => {
    if (!open || !onlineOrder || errorMsg || isProcessing) return;

    const processingType = terminalSettings?.order_qr_processing_type || ORDER_QR_PROCESSING_TYPES.MANUAL;
    if (
      processingType.toUpperCase() === ORDER_QR_PROCESSING_TYPES.AUTOMATIC &&
      onlineOrder.status?.toUpperCase() === ONLINE_ORDER_STATUS.PENDING
    ) {
      const timer = setTimeout(() => {
        proceedOrder(onlineOrder);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [open, onlineOrder, errorMsg, isProcessing, terminalSettings, proceedOrder]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!open) return null;

  const orderItems = onlineOrder?.details?.items || [];
  const customerName = onlineOrder?.details?.customer?.name || onlineOrder?.customer?.name || "Walk-in";
  const customerMobile = onlineOrder?.details?.customer?.mobile || onlineOrder?.customer?.mobile || "---";
  const tableName = onlineOrder?.details?.customer?.table;
  const orderType = onlineOrder?.details?.customer?.order_type;

  const formatSourceName = (src) => {
    if (!src) return "Sanoft Cart";
    return src
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const sourceName = formatSourceName(onlineOrder?.source);

  const getActionButtonLabel = (status) => {
    switch (status?.toUpperCase()) {
      case ONLINE_ORDER_STATUS.PENDING:
        return "ACCEPT & KOT";
      case ONLINE_ORDER_STATUS.PREPARING:
        return "MARK AS READY";
      case ONLINE_ORDER_STATUS.READY:
        return "DELIVER & PRINT";
      default:
        return "PROCEED";
    }
  };

  return (
    <div className={styles.popUp} onClick={handleBackdropClick}>
      <Grid container className={styles.billContentWrapper} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <Grid item xs={12} className={styles.headSec} mb={2}>
          <h3>Online Order Details</h3>
          <CloseIcon className={styles.icon} onClick={onClose} />
        </Grid>

        {loading ? (
          <div className={styles.loadingContainer}>
            <CircularProgress color="error" />
            <p>Loading order details...</p>
          </div>
        ) : errorMsg ? (
          <div className={styles.errorContainer}>
            <div className={styles.errorText}>{errorMsg}</div>
            <div className={styles.footSec}>
              <ButtonGray onClick={onClose}>Close</ButtonGray>
            </div>
          </div>
        ) : (
          <>
            {/* Meta info section */}
            <Grid item xs={12} className={styles.headInfoSec}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <div className={styles.priceTxt}>
                    <div className={styles.subTitle}>Order ID</div>
                    <div className={styles.colon}>:</div>
                    <div className={styles.amountGray}>{onlineOrder.id}</div>
                  </div>
                  <div className={styles.priceTxt}>
                    <div className={styles.subTitle}>Customer</div>
                    <div className={styles.colon}>:</div>
                    <div className={styles.amountGray}>{customerName}</div>
                  </div>
                  <div className={styles.priceTxt}>
                    <div className={styles.subTitle}>Contact</div>
                    <div className={styles.colon}>:</div>
                    <div className={styles.amountGray}>{customerMobile}</div>
                  </div>
                  {tableName && (
                    <div className={styles.priceTxt}>
                      <div className={styles.subTitle}>Table</div>
                      <div className={styles.colon}>:</div>
                      <div className={styles.amountGray}>{tableName}</div>
                    </div>
                  )}
                </Grid>

                <Grid item xs={6}>
                  <div className={styles.priceTxt}>
                    <div className={styles.subTitle}>Source</div>
                    <div className={styles.colon}>:</div>
                    <div className={styles.amountRed}>{sourceName}</div>
                  </div>
                  <div className={styles.priceTxt}>
                    <div className={styles.subTitle}>Status</div>
                    <div className={styles.colon}>:</div>
                    <div className={styles.statusValue}>
                      <span className={styles.statusPill}>{onlineOrder.status}</span>
                    </div>
                  </div>
                  {onlineOrder.online_payment && (
                    <div className={styles.priceTxt}>
                      <div className={styles.subTitle}>Payment</div>
                      <div className={styles.colon}>:</div>
                      <div className={styles.amountGray}>
                        {selectedShop?.currency_symbol || ""} {onlineOrder.online_payment.amount} (
                        {onlineOrder.online_payment.status})
                      </div>
                    </div>
                  )}
                  {orderType && (
                    <div className={styles.priceTxt}>
                      <div className={styles.subTitle}>Order Type</div>
                      <div className={styles.colon}>:</div>
                      <div className={styles.amountGray} style={{ textTransform: "capitalize" }}>
                        {orderType}
                      </div>
                    </div>
                  )}
                  <div className={styles.priceTxt}>
                    <div className={styles.subTitle}>Created At</div>
                    <div className={styles.colon}>:</div>
                    <div className={styles.amountGray}>{DateTimeHelpers.formatDateTime(onlineOrder.created_at)}</div>
                  </div>
                </Grid>
              </Grid>
            </Grid>

            {/* Items Table */}
            <h3 className={styles.sectionTitle}>Order Items</h3>
            <Grid item xs={12} className={styles.tableContainer}>
              <table>
                <thead>
                  <tr>
                    <th style={{ width: "10%" }}>Sl</th>
                    <th style={{ width: "50%" }}>Item Name</th>
                    <th style={{ width: "15%" }} align="right">
                      Qty
                    </th>
                    <th style={{ width: "25%" }} align="right">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {orderItems.map((item, idx) => (
                    <tr key={idx}>
                      <td style={{ width: "10%" }}>{idx + 1}</td>
                      <td style={{ width: "50%" }}>
                        {item.name} {item.option && <span className={styles.itemOption}>({item.option})</span>}
                      </td>
                      <td style={{ width: "15%" }} align="right">
                        {item.quantity}
                      </td>
                      <td style={{ width: "25%" }} align="right">
                        {selectedShop?.currency_symbol || ""} {NumberFormatHelper.roundOff(item.quantity * item.rate)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Grid>

            <div className={styles.border}></div>

            {/* Footer Buttons */}
            {onlineOrder.status?.toUpperCase() !== ONLINE_ORDER_STATUS.DELIVERED && (
              <Grid item xs={12} className={styles.footSec} mt={2}>
                <ButtonRed onClick={() => proceedOrder(onlineOrder)} disabled={isProcessing}>
                  {isProcessing ? "Processing..." : getActionButtonLabel(onlineOrder.status)}
                </ButtonRed>
              </Grid>
            )}
          </>
        )}
      </Grid>

      {/* Hidden print templates */}
      <div style={{ display: "none" }}>
        {kotPrintSale && <KOTInvoice ref={kotPrintRef} sale={kotPrintSale} />}
        {billPrintSale && <ThreeInchInvoice ref={invoicePrintRef} sale={billPrintSale} shop={selectedShop} />}
      </div>
    </div>
  );
};

export default OnlineOrderDetailsPopup;
